import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, shareReplay, catchError, retry, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type LegalTemplate = 'PRIVACY_POLICY' | 'TERMS_AND_CONDITION';

/**
 * Serves the legal documents shown at signup.
 *
 * Deliberately bypasses ApiService: that pipeline attaches a session token and
 * treats a 500 as an expired session, which would bounce a brand-new visitor
 * to /login. The admin CMS route is public and is the same source the
 * marketing site renders these documents from — but only on GET; POST there is
 * treated as an authenticated call and rejected.
 */
@Injectable({ providedIn: 'root' })
export class LegalDocumentsService {

  /** In-flight/settled requests, so a warmed document is never re-fetched. */
  private cache = new Map<LegalTemplate, Observable<string>>();

  constructor(private http: HttpClient) { }

  get(template: LegalTemplate): Observable<string> {
    const cached = this.cache.get(template);
    if (cached) {
      return cached;
    }

    const request = this.http.get<any>(`${environment.baseUrl}support/cms_details`, {
      params: { template_name: template, type: 1 }
    }).pipe(
      timeout(20000),
      // A transient network blip on page load would otherwise leave the modal
      // permanently showing its error state.
      retry({ count: 2, delay: 1000 }),
      map(response => {
        const content = response?.data?.[0]?.content;
        if (response?.is_error || !content) {
          throw new Error(`legal document unavailable: ${template} (${response?.original_text || 'no content'})`);
        }
        return content as string;
      }),
      // Replay to every later subscriber; one network call per document.
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError(err => {
        // A failed request must not be cached, or Retry could never recover.
        this.cache.delete(template);
        // Surface the real cause — a blocked cross-origin request and an
        // application error look identical from the modal otherwise.
        console.error(`[legal] failed to load ${template} from ${environment.baseUrl}`, err?.status, err?.message || err);
        return throwError(() => err);
      })
    );

    this.cache.set(template, request);
    return request;
  }

  /**
   * Starts fetching both documents ahead of time so the modal has content the
   * moment it opens. Errors are swallowed — this is a warm-up, and the modal
   * surfaces real failures itself when it subscribes.
   */
  preload(): void {
    (['PRIVACY_POLICY', 'TERMS_AND_CONDITION'] as LegalTemplate[])
      .forEach(t => this.get(t).pipe(catchError(() => of(''))).subscribe());
  }

  /** Drops a cached document so the next get() re-fetches it. */
  invalidate(template: LegalTemplate): void {
    this.cache.delete(template);
  }
}
